import React from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import { FormattedMessage } from 'react-intl-next';
import type { SingleValue } from 'react-select';

import CheckIcon from '@atlaskit/icon/glyph/check';
import { Box, Inline, Text, type TextColor } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import type { OptionProps, SingleValueProps, StylesConfig } from '@atlaskit/select';
import Select, { components } from '@atlaskit/select';

export interface ModeOption {
	value: string;
	icon: React.ReactNode;
	message: MessageDescriptor | string;
}

export interface ModeSwitcherProps {
	selectedValue: string;
	options: ModeOption[];
	onChange?: (value: string) => void;
	isMenuOpen?: boolean;
	isDisabled?: boolean;
	isCompact?: boolean;
}

const switcherDropdownStyles: StylesConfig<ModeOption, false> = {
	menu: (base) => ({
		...base,
		minWidth: '140px',
	}),
	control: (base, state) => ({
		...base,
		minWidth: '115px',
		':hover': {
			...base[':hover'],
			borderColor: state.isFocused ? token('color.border.selected') : 'transparent',
		},
	}),
	dropdownIndicator: (base) => ({
		...base,
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
	}),
	valueContainer: (base) => ({
		...base,
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
	}),
};

const OptionLabel = ({
	option,
	isSelected,
	inDropdown,
	isDisabled,
}: {
	option: ModeOption;
	isSelected: boolean;
	inDropdown: boolean;
	isDisabled?: boolean;
}) => {
	const messageContent =
		typeof option.message === 'string' ? option.message : <FormattedMessage {...option.message} />;

	let textColor: TextColor = 'color.text';
	if (isDisabled) textColor = 'color.text.disabled';
	else if (inDropdown && isSelected) textColor = 'color.text.selected';

	const alignInline = inDropdown ? 'start' : 'center';
	const space = inDropdown ? 'space.100' : 'space.050';

	return (
		<Inline
			alignBlock="center"
			alignInline={alignInline}
			shouldWrap={false}
			grow="fill"
			space={space}
			testId={`mode-switcher-option-${option.value}`}
		>
			{option.icon}
			<Box>
				<Text size="medium" color={textColor}>
					{messageContent}
				</Text>
			</Box>
			{inDropdown && isSelected && <CheckIcon label="Selected" />}
		</Inline>
	);
};

const CustomSingleValue = (props: SingleValueProps<ModeOption>) => (
	<components.SingleValue {...props}>
		<OptionLabel option={props.data} isSelected inDropdown={false} isDisabled={props.isDisabled} />
	</components.SingleValue>
);

const CustomOption = (props: OptionProps<ModeOption>) => {
	const { data, isSelected } = props;
	return (
		<components.Option {...props}>
			<OptionLabel option={data} isSelected={isSelected} inDropdown isDisabled={props.isDisabled} />
		</components.Option>
	);
};

export const ModeSwitcher = ({
	selectedValue,
	options,
	onChange,
	isMenuOpen,
	isDisabled,
	isCompact,
}: ModeSwitcherProps) => {
	const handleOptionChange = (option: SingleValue<ModeOption>) => {
		if (option) onChange?.(option.value);
	};

	const selectedOption = options.find((option) => option.value === selectedValue);

	return (
		<Select
			options={options}
			styles={switcherDropdownStyles}
			value={selectedOption}
			onChange={handleOptionChange}
			appearance="subtle"
			isSearchable={false}
			components={{
				SingleValue: CustomSingleValue,
				Option: CustomOption,
				IndicatorSeparator: null,
			}}
			blurInputOnSelect
			menuIsOpen={isMenuOpen}
			isDisabled={isDisabled}
			spacing={isCompact ? 'compact' : 'default'}
		/>
	);
};
